/**
 * Skills Intelligence Dashboard
 * 
 * Comprehensive analytics view combining:
 * - Skills Matrix (staff × skills heatmap)
 * - Gap Analysis (skills at risk)
 * - SPOF Detection (single points of failure)
 * - Bottleneck Identification (high expertise + high utilization)
 * - AI-powered insights and recommendations
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, TrendingDown, Users, Zap, AlertCircle, CheckCircle, Sparkles } from 'lucide-react';
import { getSkillsMatrix, getThemisRecommendations, type MatrixResponse, type ThemisRecommendation } from '../services/staffService';
import { Badge } from './ui/badge';
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface MetricCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  severity?: 'critical' | 'warning' | 'success' | 'info';
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, severity = 'info' }) => {
  const severityColors = {
    critical: 'border-red-500 bg-red-950/30',
    warning: 'border-yellow-500 bg-yellow-950/30',
    success: 'border-green-500 bg-green-950/30',
    info: 'border-primary-500 bg-primary-950/30',
  };

  return (
    <div className={`p-6 rounded-lg border ${severityColors[severity]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-neutral-400">{title}</p>
          <p className="text-3xl font-bold text-white mt-2">{value}</p>
        </div>
        <div className="text-primary-500">{icon}</div>
      </div>
    </div>
  );
};

export const SkillsIntelligenceDashboard: React.FC = () => {
  const [matrixData, setMatrixData] = useState<MatrixResponse | null>(null);
  const [recommendations, setRecommendations] = useState<ThemisRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load skills matrix data
      const matrixResponse = await getSkillsMatrix();
      if (matrixResponse.success && matrixResponse.data) {
        setMatrixData(matrixResponse.data);
      } else {
        throw new Error(matrixResponse.error || 'Failed to load skills matrix');
      }

      // Load Themis recommendations
      const themisResponse = await getThemisRecommendations('skills');
      setRecommendations(themisResponse.recommendations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    }
    
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-neutral-400">Loading skills intelligence...</div>
      </div>
    );
  }

  if (error || !matrixData) {
    // Check if it's a "no data" error specifically
    const isNoDataError = 
      error?.includes('No staff data found') || 
      error?.includes('No staff') ||
      error?.includes('No employee') ||
      error?.includes('404') ||
      (!error && !matrixData); // Also treat missing data without error as empty state
    
    if (isNoDataError) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="max-w-md text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-neutral-800 rounded-full flex items-center justify-center">
              <Users className="w-8 h-8 text-neutral-500" />
            </div>
            <h3 className="text-xl font-semibold text-white">No Employee or Skills Data</h3>
            <p className="text-neutral-400">
              Skills Intelligence requires staff and skills data to generate analytics. 
              Populate the capture section to get started with team insights.
            </p>
            <div className="flex flex-col gap-3 mt-6">
              <a 
                href="/devtools" 
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-black font-medium rounded-lg transition-colors"
              >
                <Zap className="w-4 h-4" />
                Go to DevTools
              </a>
              <p className="text-sm text-neutral-500">
                Click "Populate Mock Data" to add sample staff and skills
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    // Generic error state
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <div className="text-red-400">Error loading skills intelligence</div>
          <div className="text-sm text-neutral-500">{error || 'Unknown error occurred'}</div>
          <button 
            onClick={loadData}
            className="mt-4 px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { summary, gapAnalysis, matrix } = matrixData;

  // Get score badge variant
  const getScoreBadge = (score: number): React.ReactNode => {
    const roundedScore = Math.round(score);
    if (score >= 75) return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">{roundedScore}</Badge>;
    if (score >= 50) return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">{roundedScore}</Badge>;
    if (score >= 25) return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">{roundedScore}</Badge>;
    return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">{roundedScore}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Themis Insights - Intelligence First! */}
      <div className="bg-gradient-to-r from-primary-950/30 to-cyan-950/30 border border-primary-500/30 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary-400" />
          Themis Insights
        </h3>
        <div className="space-y-3">
          {summary.spofCount > 0 && (
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
              <div>
                <p className="text-white font-medium">Critical Succession Risk Detected</p>
                <p className="text-neutral-300 text-sm">
                  You have {summary.spofCount} single point(s) of failure. Prioritize cross-training programs
                  to mitigate knowledge concentration risks.
                </p>
              </div>
            </div>
          )}
          {summary.bottleneckCount > 0 && (
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-orange-500 mt-0.5" />
              <div>
                <p className="text-white font-medium">Capacity Bottlenecks Identified</p>
                <p className="text-neutral-300 text-sm">
                  {summary.bottleneckCount} high-expertise staff member(s) are at or near capacity. 
                  Consider workload rebalancing or hiring to prevent burnout.
                </p>
              </div>
            </div>
          )}
          {summary.skillsAtRisk > 0 && (
            <div className="flex items-start gap-3">
              <TrendingDown className="w-5 h-5 text-red-500 mt-0.5" />
              <div>
                <p className="text-white font-medium">Skills Gap Analysis</p>
                <p className="text-neutral-300 text-sm">
                  {summary.skillsAtRisk} skill(s) have below-average team proficiency. 
                  Invest in training programs or strategic hiring to strengthen these capabilities.
                </p>
              </div>
            </div>
          )}
          {summary.spofCount === 0 && summary.skillsAtRisk === 0 && summary.bottleneckCount === 0 && (
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <p className="text-white font-medium">Healthy Skills Distribution</p>
                <p className="text-neutral-300 text-sm">
                  Your team shows good capability distribution with no critical gaps or bottlenecks. 
                  Continue with regular skills development and succession planning.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Skills at Risk"
          value={summary.skillsAtRisk}
          icon={<TrendingDown className="w-8 h-8" />}
          severity={summary.skillsAtRisk > 3 ? 'critical' : summary.skillsAtRisk > 0 ? 'warning' : 'success'}
        />
        <MetricCard
          title="SPOF Count"
          value={summary.spofCount}
          icon={<AlertTriangle className="w-8 h-8" />}
          severity={summary.spofCount > 5 ? 'critical' : summary.spofCount > 0 ? 'warning' : 'success'}
        />
        <MetricCard
          title="Bottlenecks"
          value={summary.bottleneckCount}
          icon={<Zap className="w-8 h-8" />}
          severity={summary.bottleneckCount > 3 ? 'warning' : 'info'}
        />
        <MetricCard
          title="Avg Expertise"
          value={Math.round(summary.avgExpertiseScore)}
          icon={<Users className="w-8 h-8" />}
          severity={summary.avgExpertiseScore >= 70 ? 'success' : summary.avgExpertiseScore >= 50 ? 'info' : 'warning'}
        />
      </div>

      {/* Gap Analysis Section */}
      {gapAnalysis.length > 0 && (
        <div className="bg-neutral-950 border border-neutral-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-500" />
            Gap Analysis - Skills Requiring Attention
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-800">
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium">Skill</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium">Category</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium">Avg Score</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium">Staff Count</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium">Risk Level</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium">Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {gapAnalysis.map((gap) => (
                  <tr key={gap.skillId} className="border-b border-neutral-800 hover:bg-neutral-900/50">
                    <td className="py-3 px-4 text-white font-medium">{gap.skillName}</td>
                    <td className="py-3 px-4 text-neutral-300">{gap.category}</td>
                    <td className="py-3 px-4">{getScoreBadge(gap.avgScore)}</td>
                    <td className="py-3 px-4 text-neutral-300">{gap.staffCount}</td>
                    <td className="py-3 px-4">
                      {gap.riskLevel === 'critical' && (
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Critical</Badge>
                      )}
                      {gap.riskLevel === 'high' && (
                        <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">High</Badge>
                      )}
                      {gap.riskLevel === 'medium' && (
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Medium</Badge>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-neutral-400">
                      {gap.avgScore < 30 ? 'Hire expert or provide intensive training' :
                       gap.avgScore < 50 ? 'Training program or mentorship' :
                       'Continuous development'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Themis Recommendations - SPOF & Bottleneck Risks */}
      {recommendations.length > 0 && (
        <div className="bg-neutral-950 border border-neutral-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Themis Recommendations - Critical Actions Required
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-800">
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium">Staff Member</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium">Skill</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium">Expertise</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium">Themis Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {recommendations.filter(r => r.type === 'spof' || r.type === 'bottleneck').map((rec) => (
                  <tr key={rec.id} className="border-b border-neutral-800 hover:bg-neutral-900/50">
                    <td className="py-3 px-4 text-white font-medium">{rec.context.staffMember}</td>
                    <td className="py-3 px-4 text-neutral-300">{rec.context.skill || rec.context.department}</td>
                    <td className="py-3 px-4">
                      {rec.context.expertiseScore ? getScoreBadge(rec.context.expertiseScore) : '-'}
                    </td>
                    <td className="py-3 px-4">
                      {rec.severity === 'critical' ? (
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                          {rec.context.isBottleneck ? 'SPOF + Bottleneck' : rec.type.toUpperCase()}
                        </Badge>
                      ) : (
                        <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                          {rec.type.toUpperCase()}
                        </Badge>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-neutral-300">
                      {rec.action}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Skills Matrix - Radar Chart by Department */}
      <div className="bg-neutral-950 border border-neutral-800 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-primary-500" />
          Skills Matrix - Department Capability Radar
        </h3>
        <p className="text-sm text-neutral-400 mb-6">
          Overall skill proficiency across departments - each axis represents a department showing average score across all skills
        </p>
        
        {(() => {
          // Calculate average score across ALL skills for each department
          const departmentAverages = new Map<string, { totalScore: number; count: number }>();
          
          // Process matrix data to get department-level averages
          matrix.forEach(staff => {
            if (!departmentAverages.has(staff.department)) {
              departmentAverages.set(staff.department, { totalScore: 0, count: 0 });
            }
            const deptData = departmentAverages.get(staff.department)!;
            
            staff.skills.forEach(skill => {
              deptData.totalScore += skill.expertiseScore;
              deptData.count++;
            });
          });

          // Transform to radar chart data: one data point per department
          const radarData = Array.from(departmentAverages.entries()).map(([dept, data]) => ({
            department: dept,
            averageScore: data.count > 0 ? Math.round(data.totalScore / data.count) : 0
          }));

          return (
            <div className="space-y-6">
              <div className="h-[500px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#404040" />
                    <PolarAngleAxis 
                      dataKey="department" 
                      tick={{ fill: '#a3a3a3', fontSize: 12 }}
                    />
                    <PolarRadiusAxis 
                      angle={90} 
                      domain={[0, 100]}
                      tick={{ fill: '#a3a3a3' }}
                      tickFormatter={(value) => `${value}`}
                    />
                    <Radar
                      name="Average Skill Score"
                      dataKey="averageScore"
                      stroke="#00D4FF"
                      fill="#00D4FF"
                      fillOpacity={0.25}
                      strokeWidth={3}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#171717',
                        border: '1px solid #404040',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value: any) => [`${value}`, 'Avg Score']}
                    />
                    <Legend 
                      wrapperStyle={{
                        paddingTop: '20px'
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              {/* Summary Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-800">
                      <th className="text-left py-2 px-3 text-neutral-400 font-medium">Department</th>
                      <th className="text-center py-2 px-3 text-neutral-400 font-medium">Average Score</th>
                      <th className="text-center py-2 px-3 text-neutral-400 font-medium">Total Skills Assessed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {radarData.map((dept) => (
                      <tr 
                        key={dept.department} 
                        className="border-b border-neutral-800/50 hover:bg-neutral-800/30 cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedDepartment(dept.department);
                          setIsPanelOpen(true);
                        }}
                      >
                        <td className="py-2 px-3 text-white font-medium">{dept.department}</td>
                        <td className="py-2 px-3 text-center">
                          <span className="text-primary-400 font-semibold">{dept.averageScore}</span>
                        </td>
                        <td className="py-2 px-3 text-center text-neutral-400">
                          {Array.from(departmentAverages.entries()).find(([d]) => d === dept.department)?.[1].count || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Department Detail Slide-Out Panel - Rendered via Portal */}
      {isPanelOpen && selectedDepartment && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsPanelOpen(false)}
          />
          
          {/* Slide-out Panel */}
          <div className="fixed right-0 top-0 h-screen w-[600px] bg-neutral-950 border-l border-neutral-800 shadow-2xl z-50 overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedDepartment}</h2>
                  <p className="text-sm text-neutral-400 mt-1">Skills Breakdown</p>
                </div>
                <button
                  onClick={() => setIsPanelOpen(false)}
                  className="text-neutral-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Department Skills Radar */}
              {(() => {
                // Get all staff in selected department
                const deptStaff = matrixData.matrix.filter(s => s.department === selectedDepartment);
                
                // Calculate average score per skill for this department
                const skillScores = new Map<string, { skillName: string; totalScore: number; count: number }>();
                
                deptStaff.forEach(staff => {
                  staff.skills.forEach(skill => {
                    if (!skillScores.has(skill.skillId)) {
                      skillScores.set(skill.skillId, {
                        skillName: skill.skillName,
                        totalScore: 0,
                        count: 0
                      });
                    }
                    const data = skillScores.get(skill.skillId)!;
                    data.totalScore += skill.expertiseScore;
                    data.count++;
                  });
                });

                // Transform to radar data
                const deptRadarData = Array.from(skillScores.entries()).map(([_, data]) => ({
                  skill: data.skillName,
                  score: data.count > 0 ? Math.round(data.totalScore / data.count) : 0,
                  staffCount: data.count
                }));

                const overallAvg = deptRadarData.length > 0
                  ? Math.round(deptRadarData.reduce((sum, s) => sum + s.score, 0) / deptRadarData.length)
                  : 0;

                return (
                  <>
                    {/* Summary Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-neutral-900 rounded-lg p-4 border border-neutral-800">
                        <p className="text-xs text-neutral-400 mb-1">Staff Count</p>
                        <p className="text-2xl font-bold text-white">{deptStaff.length}</p>
                      </div>
                      <div className="bg-neutral-900 rounded-lg p-4 border border-neutral-800">
                        <p className="text-xs text-neutral-400 mb-1">Skills Tracked</p>
                        <p className="text-2xl font-bold text-white">{skillScores.size}</p>
                      </div>
                      <div className="bg-neutral-900 rounded-lg p-4 border border-neutral-800">
                        <p className="text-xs text-neutral-400 mb-1">Avg Score</p>
                        <p className="text-2xl font-bold text-primary-400">{overallAvg}</p>
                      </div>
                    </div>

                    {/* Radar Chart - Only show if 3 or more skills */}
                    {deptRadarData.length >= 3 ? (
                      <div className="bg-neutral-900 rounded-lg p-6 border border-neutral-800 mb-6">
                        <h3 className="text-lg font-semibold text-white mb-4">Skills Proficiency</h3>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={deptRadarData}>
                              <PolarGrid stroke="#404040" />
                              <PolarAngleAxis 
                                dataKey="skill" 
                                tick={{ fill: '#a3a3a3', fontSize: 10 }}
                              />
                              <PolarRadiusAxis 
                                angle={90} 
                                domain={[0, 100]}
                                tick={{ fill: '#a3a3a3', fontSize: 10 }}
                              />
                              <Radar
                                name="Skill Score"
                                dataKey="score"
                                stroke="#00D4FF"
                                fill="#00D4FF"
                                fillOpacity={0.3}
                                strokeWidth={2}
                              />
                              <Tooltip 
                                contentStyle={{
                                  backgroundColor: '#171717',
                                  border: '1px solid #404040',
                                  borderRadius: '8px',
                                  color: '#fff'
                                }}
                                formatter={(value: any) => [`${value}`, 'Score']}
                              />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-neutral-900 rounded-lg p-6 border border-neutral-800 mb-6">
                        <h3 className="text-lg font-semibold text-white mb-4">Skills Proficiency</h3>
                        <p className="text-neutral-400 text-center py-8">
                          Insufficient data for radar visualization (minimum 3 skills required)
                        </p>
                      </div>
                    )}

                    {/* Skills List */}
                    <div className="bg-neutral-900 rounded-lg p-6 border border-neutral-800">
                      <h3 className="text-lg font-semibold text-white mb-4">Skills Detail</h3>
                      <div className="space-y-3">
                        {deptRadarData.sort((a, b) => b.score - a.score).map((skill) => (
                          <div key={skill.skill} className="flex items-center justify-between p-3 bg-neutral-950 rounded border border-neutral-800">
                            <div className="flex-1">
                              <p className="text-white font-medium">{skill.skill}</p>
                              <p className="text-xs text-neutral-400 mt-1">{skill.staffCount} staff member{skill.staffCount !== 1 ? 's' : ''}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-32 bg-neutral-800 rounded-full h-2">
                                <div 
                                  className="bg-primary-500 h-2 rounded-full"
                                  style={{ width: `${skill.score}%` }}
                                />
                              </div>
                              <span className="text-primary-400 font-semibold w-12 text-right">{skill.score}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default SkillsIntelligenceDashboard;
